import React, { useState, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import useApiRequest from '../hooks/useApiRequest'
import BonusHistorySection from './BonusHistorySection'
import RemoveBrigadeButton from './RemoveBrigadeButton'
import LabelButtons from './LabelButtons'
import WtnSelector from './WtnSelector'
import ShowMoreButton from './ShowMoreButton'
import LabelNote from './LabelNote'
import BrigadeLabelRequestModalWrapper from '../Modals/BrigadeLabelRequestModalWrapper'
import PointsSection from './PointsSection'
import BrigadeHeader from './BrigadeHeader'
import LabelTable from './LabelTable'
import { SanitizeHTML } from '@tc-packages/tc-common'

const Brigade = ({
  bonusesPath,
  brigadeDescription,
  brigadeLabelLimitIsReached,
  brigadePath,
  clearLabelMessagePath,
  collectionLabelLimitIsReached,
  commonPointsAllocation,
  downloadResourcesPath,
  dutyCareLink,
  hasBonus,
  id,
  labels,
  leaveBrigadePath,
  name,
  openRequestedLabelsOnLoad,
  pointsPerShipment,
  requestLabelPath,
  shipmentMinWeightUnits,
  shipmentMinWeightValue,
  shipmentsPath,
  shippingModelData,
  t,
  wtnIds,
  wtnPdfPath,
  locationNeedReview,
}) => {
  const [isShowingBonusHistory, setIsShowingBonusHistory] = useState(false)
  const [isShowingLabels, setIsShowingLabels] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState({
    name: '',
    text: '',
    subheader: '',
    shipper: '',
    shipperIconUrl: '',
    type: 'success',
  })

  const containerRef = useRef(null)

  const { isLoading, sendRequest } = useApiRequest()

  const toggleBonusHistory = useCallback((e) => {
    e.preventDefault()
    setIsShowingBonusHistory((prevState) => !prevState)
  }, [])

  const toggleLabel = useCallback((e) => {
    e.preventDefault()
    setIsShowingLabels((prevState) => !prevState)
  }, [])

  useEffect(() => {
    if (!openRequestedLabelsOnLoad) return

    setIsShowingLabels(true)

    window.requestAnimationFrame(() => {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }, [openRequestedLabelsOnLoad])

  const requestLabel = async (
    requestPath,
    shippingModelId,
    successMessage,
    shipper,
    shipperIconUrl,
  ) => {
    try {
      await sendRequest(requestPath, 'POST', {
        label_request: { shipping_model_id: shippingModelId },
      })
      setModalContent({
        name,
        subheader: t['flash.users.label_request_received_subheader'],
        text: successMessage,
        shipper,
        shipperIconUrl,
        type: 'success',
      })
      setIsModalOpen(true)
    } catch (error) {
      setModalContent({
        name,
        subheader: '',
        text: t['tcred.label.request.failed.body'],
        shipper,
        shipperIconUrl,
        type: 'error',
      })
      setIsModalOpen(true)
    }
  }

  const handleLabelRequest = (data) => {
    const shipper = data?.shipper || ''
    const shipperIconUrl = data?.shipper_icon_url || ''

    if (data.confirmation_message) {
      if (window.confirm(data.confirmation_message)) {
        requestLabel(
          requestLabelPath,
          data.id,
          data.success_message,
          shipper,
          shipperIconUrl,
        )
      }
    } else {
      requestLabel(
        requestLabelPath,
        data.id,
        data.success_message,
        shipper,
        shipperIconUrl,
      )
    }
  }

  const renderDownloadLabelButton = (labelUrl) => (
    <a
      className="shipmentHistory_requestLabel"
      href={labelUrl}
      rel="noreferrer"
      target="_blank"
    >
      {t['tcnext.download_label']}
    </a>
  )

  return (
    <div ref={containerRef}>
      <RemoveBrigadeButton path={leaveBrigadePath} t={t} />

      <div className="activeCollection_lable mobile active">
        <span>{t['tcred.collection.active']}</span>
      </div>

      <div className="activeCollection_mainBlock toggledBody">
        <BrigadeHeader
          brigadeDescription={brigadeDescription}
          brigadePath={brigadePath}
          name={name}
          t={t}
        />

        <PointsSection
          commonPointsAllocation={commonPointsAllocation}
          pointsPerShipment={pointsPerShipment}
          shipmentMinWeightUnits={shipmentMinWeightUnits}
          shipmentMinWeightValue={shipmentMinWeightValue}
          t={t}
        />

        <LabelNote
          brigadeLabelLimitIsReached={brigadeLabelLimitIsReached}
          collectionLabelLimitIsReached={collectionLabelLimitIsReached}
          labelsLength={labels.length}
          t={t}
        />

        {labels.length === 0 && shippingModelData.length > 1 && (
          <SanitizeHTML
            className="activeCollection_explainerText"
            html={t['tcred.ready_to_ship_without_label']}
          />
        )}

        {locationNeedReview && (
          <SanitizeHTML
            className="activeCollection_explainerText warning"
            html={`<span style="font-weight:bold;">⚠️</span> ${t['tcred.profile.location_needs_review']}`}
          />
        )}

        <LabelButtons
          brigadeLabelLimitIsReached={brigadeLabelLimitIsReached}
          collectionLabelLimitIsReached={collectionLabelLimitIsReached}
          downloadResourcesPath={downloadResourcesPath}
          handleLabelRequest={handleLabelRequest}
          isLoading={isLoading}
          isShowingLabels={isShowingLabels}
          labels={labels}
          renderDownloadLabelButton={renderDownloadLabelButton}
          shipmentsPath={shipmentsPath}
          shippingModelData={shippingModelData}
          t={t}
          toggleLabel={toggleLabel}
        />

        <LabelTable
          brigadeLabelLimitIsReached={brigadeLabelLimitIsReached}
          collectionLabelLimitIsReached={collectionLabelLimitIsReached}
          handleLabelRequest={handleLabelRequest}
          isLoading={isLoading}
          isShowingLabels={isShowingLabels}
          labels={labels}
          renderDownloadLabelButton={renderDownloadLabelButton}
          shippingModelData={shippingModelData}
          t={t}
        />

        <WtnSelector
          dutyCareLink={dutyCareLink}
          id={id}
          t={t}
          wtnIds={wtnIds}
          wtnPdfPath={wtnPdfPath}
        />

        <BonusHistorySection
          bonusesPath={bonusesPath}
          hasBonus={hasBonus}
          isShowingBonusHistory={isShowingBonusHistory}
          t={t}
          toggleBonusHistory={toggleBonusHistory}
        />
      </div>

      <ShowMoreButton t={t} />

      <BrigadeLabelRequestModalWrapper
        postUrl={clearLabelMessagePath}
        shipper={modalContent.shipper}
        shipperIconUrl={modalContent.shipperIconUrl}
        shouldReloadOnProfilePage
        subheader={modalContent.subheader}
        text={modalContent.text}
        type={modalContent.type}
      />
    </div>
  )
}

Brigade.propTypes = {
  bonusesPath: PropTypes.string.isRequired,
  brigadeDescription: PropTypes.string,
  brigadeLabelLimitIsReached: PropTypes.bool,
  brigadePath: PropTypes.string,
  clearLabelMessagePath: PropTypes.string.isRequired,
  collectionLabelLimitIsReached: PropTypes.bool,
  commonPointsAllocation: PropTypes.bool,
  downloadResourcesPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  dutyCareLink: PropTypes.string,
  hasBonus: PropTypes.bool,
  hasLastShipment: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labels: PropTypes.array,
  leaveBrigadePath: PropTypes.string,
  locationNeedReview: PropTypes.bool,
  name: PropTypes.string,
  openRequestedLabelsOnLoad: PropTypes.bool,
  pointsPerShipment: PropTypes.string,
  requestLabelPath: PropTypes.string,
  shipmentMinWeightUnits: PropTypes.string,
  shipmentMinWeightValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  shipmentsPath: PropTypes.string,
  shippingModelData: PropTypes.array,
  t: PropTypes.object.isRequired,
  wtnIds: PropTypes.array,
  wtnPdfPath: PropTypes.string,
}

export default Brigade
